import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TeamMember, MemberProposal, AvatarColor } from '@/types';
import { api } from '@/lib/api';
import { useAuthStore } from './useAuthStore';
import { toast } from 'sonner';

interface TeamStore {
  // State
  members: TeamMember[];
  hasOnboarded: boolean;
  lastSyncedUserId: string | null;

  // Member actions
  addMember: (proposal: MemberProposal, preGeneratedAvatarUrl?: string) => TeamMember;
  updateMember: (id: string, updates: Partial<Omit<TeamMember, 'id' | 'discoveredAt'>>) => void;
  deleteMember: (id: string) => Promise<void>;

  // Settings
  setOnboarded: () => void;
  setLastSyncedUserId: (userId: string | null) => void;
  clearLocal: () => void;
}

const AVATAR_COLORS: AvatarColor[] = [
  'amber', 'rose', 'violet', 'sky', 'emerald',
  'orange', 'teal', 'fuchsia', 'lime', 'indigo',
];

function pickColor(existingMembers: TeamMember[]): AvatarColor {
  const used = existingMembers.map((m) => m.avatarColor);
  const unused = AVATAR_COLORS.filter((c) => !used.includes(c));
  const pool = unused.length > 0 ? unused : AVATAR_COLORS;
  return pool[Math.floor(Math.random() * pool.length)];
}

function isLoggedIn(): boolean {
  return useAuthStore.getState().user !== null;
}

export const useTeamStore = create<TeamStore>()(
  persist(
    (set, get) => ({
      members: [],
      hasOnboarded: false,
      lastSyncedUserId: null,

      addMember: (proposal, preGeneratedAvatarUrl) => {
        const member: TeamMember = {
          id: crypto.randomUUID(),
          name: proposal.name,
          role: proposal.role,
          description: proposal.description,
          values: proposal.values,
          typicalPhrases: proposal.typicalPhrases,
          avatarEmoji: proposal.suggestedEmoji,
          avatarColor: proposal.suggestedColor ?? pickColor(get().members),
          appearanceDescription: proposal.appearanceDescription,
          avatarImageUrl: preGeneratedAvatarUrl,
          discoveredAt: new Date().toISOString(),
        };
        set((state) => ({ members: [...state.members, member] }));

        // Only generate if not already pre-generated in the proposal card
        if (!preGeneratedAvatarUrl && proposal.appearanceDescription) {
          api.avatar.generate(member.id, proposal.appearanceDescription)
            .then(({ avatarImageUrl }) => {
              set((state) => ({
                members: state.members.map((m) =>
                  m.id === member.id ? { ...m, avatarImageUrl } : m,
                ),
              }));
            })
            .catch((err) => {
              console.error('Avatar generation failed:', err);
              toast.error('Avatar-Generierung fehlgeschlagen.');
            });
        }

        if (isLoggedIn()) {
          api.team.create(member).catch(console.error);
        }
        return member;
      },

      updateMember: (id, updates) => {
        set((state) => ({
          members: state.members.map((m) =>
            m.id === id ? { ...m, ...updates } : m,
          ),
        }));
        if (isLoggedIn()) {
          const updated = get().members.find((m) => m.id === id);
          if (updated) api.team.update(id, updated).catch(console.error);
        }
      },

      deleteMember: async (id) => {
        const previous = get().members;
        set((state) => ({ members: state.members.filter((m) => m.id !== id) }));
        if (isLoggedIn()) {
          try {
            await api.team.delete(id);
          } catch (err) {
            set({ members: previous });
            throw err;
          }
        }
      },

      setOnboarded: () => set({ hasOnboarded: true }),

      setLastSyncedUserId: (userId) => set({ lastSyncedUserId: userId }),

      clearLocal: () => set({ members: [], hasOnboarded: false, lastSyncedUserId: null }),
    }),
    {
      name: 'inner-team-v1',
      partialize: (state) => ({
        members: state.members,
        hasOnboarded: state.hasOnboarded,
        lastSyncedUserId: state.lastSyncedUserId,
      }),
    },
  ),
);
